/**
 * Money handling for the payment path (AED).
 *
 * Amounts are held as integer fils (100 fils = 1 dirham) so that adding up the
 * selected accounts is exact by construction - no drift, therefore no
 * corrective rounding needed in the middle. Integers are exact up to
 * 2^53 - 1, i.e. ~AED 90 trillion in fils, so range is not a concern.
 *
 * Floats only appear at the two boundaries:
 *   - coming in: billing API responses, which arrive as JSON floats and can
 *     carry IEEE-754 drift (10.799999999999999);
 *   - going out: the payment gateway payload, which rejects any amount with
 *     more than 2 decimal places.
 *
 * Note on the `Math.round((v + Number.EPSILON) * 100) / 100` idiom: EPSILON
 * (~2.22e-16) is the gap between adjacent doubles near 1. Above ~2 the gap
 * between doubles is larger than EPSILON, so the addition rounds straight back
 * to the same double and the correction is a no-op. `filsFromFloat` below
 * trims to 15 significant digits instead, which works at any magnitude.
 */

export const FILS_PER_DIRHAM = 100;

/** Digits with optional thousands grouping and optional decimals - nothing else. */
const MONEY_PATTERN = /^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/;

/**
 * Converts float dirhams to integer fils. Boundary use only - any float
 * arithmetic done afterwards reintroduces drift.
 *
 * Doubles carry ~15-17 significant digits and the drift lives in digits 16-17,
 * so trimming to 15 snaps 267.49999999999997 back to 267.5 before rounding,
 * regardless of magnitude. The sign is handled explicitly so that negative
 * amounts (credits, adjustments) round symmetrically with positive ones -
 * Math.round on its own rounds halves toward +Infinity.
 *
 * @returns integer fils, or null if the value is not a finite number
 */
export const filsFromFloat = (dirhams: number): number | null => {
  if (!Number.isFinite(dirhams)) return null;
  const sign = dirhams < 0 ? -1 : 1;
  const magnitude = Math.abs(dirhams) * FILS_PER_DIRHAM;
  return sign * Math.round(Number(magnitude.toPrecision(15)));
};

/**
 * Converts a money string to integer fils, all or nothing.
 *
 * parseFloat is deliberately not used: it stops at the first invalid character,
 * so parseFloat("1,234.56") returns 1 - a wrong amount rather than a failure.
 * Anything malformed here returns null so the caller can reject it, and the
 * digits are combined with integer arithmetic so no float is involved.
 *
 * @returns integer fils, or null if the string is not a well-formed amount
 */
export const filsFromString = (value: string): number | null => {
  const trimmed = value.trim();
  if (!MONEY_PATTERN.test(trimmed)) return null;

  const sign = trimmed.startsWith("-") ? -1 : 1;
  const unsigned = trimmed.replace(/^[+-]/, "").replace(/,/g, "");
  const [whole, fraction = ""] = unsigned.split(".");

  // First two fraction digits are the fils; the third decides the rounding
  // (half away from zero, applied to the magnitude so signs stay symmetric).
  const fils = Number(whole) * FILS_PER_DIRHAM + Number(fraction.padEnd(2, "0").slice(0, 2));
  const roundUp = Number(fraction.charAt(2) || "0") >= 5;
  const magnitude = fils + (roundUp ? 1 : 0);

  return Number.isSafeInteger(magnitude) ? sign * magnitude : null;
};

/**
 * Converts anything the API or a user input may hand us to integer fils.
 *
 * @returns integer fils, or null if the value is missing or malformed. Callers
 *   on the payment path should treat null as an error rather than as 0: a
 *   corrupted upstream balance must not become a plausible AED 0.00 payment.
 */
export const toFils = (value: unknown): number | null => {
  if (typeof value === "number") return filsFromFloat(value);
  if (typeof value === "string") return filsFromString(value);
  return null;
};

/** True when every value is a well-formed amount. */
export const areValidAmounts = (values: unknown[]): boolean =>
  values.every((value) => toFils(value) !== null);

/**
 * Exact sum in fils. Malformed or missing values are skipped, which keeps
 * display totals rendering; validate with `areValidAmounts` before submitting.
 */
export const sumFils = (values: unknown[]): number =>
  values.reduce<number>((total, value) => total + (toFils(value) ?? 0), 0);

/**
 * Integer fils back to a number with at most 2 decimal places.
 *
 * JS prints numbers using their shortest round-trip representation, so the
 * result serialises as "10.8" / "10.85" and never as "10.800000000000001".
 * Use `filsToAmountString` where the gateway wants a fixed 2-decimal form.
 */
export const filsToAmount = (fils: number): number => fils / FILS_PER_DIRHAM;

/** Integer fils to a fixed 2-decimal string built from integers: 1080 -> "10.80". */
export const filsToAmountString = (fils: number): string => {
  const sign = fils < 0 ? "-" : "";
  const magnitude = Math.abs(Math.trunc(fils));
  const whole = Math.floor(magnitude / FILS_PER_DIRHAM);
  const remainder = magnitude % FILS_PER_DIRHAM;
  return `${sign}${whole}.${String(remainder).padStart(2, "0")}`;
};

/**
 * Rounds an incoming amount to a clean 2-decimal number, dropping any drift.
 * Boundary sanitiser for values arriving from the billing API.
 *
 * @returns the rounded number, or null if the value is malformed
 */
export const normalizeAmount = (value: unknown): number | null => {
  const fils = toFils(value);
  return fils === null ? null : filsToAmount(fils);
};

/**
 * Same as `normalizeAmount` but keeps the value in string form, so call sites
 * that already send strings to the API keep their existing payload shape.
 */
export const normalizeAmountString = (value: unknown): string | null => {
  const fils = toFils(value);
  return fils === null ? null : filsToAmountString(fils);
};

/**
 * Normalises an amount for the wire while keeping its original type, so call
 * sites that already send strings keep sending strings and those that send
 * numbers keep sending numbers.
 *
 * A value that cannot be parsed is passed through untouched: this runs over
 * whole payloads, and silently rewriting an unrecognised field would hide the
 * problem rather than surface it.
 */
export const normalizeAmountLike = (value: unknown): unknown => {
  const fils = toFils(value);
  if (fils === null) return value;
  return typeof value === "string" ? filsToAmountString(fils) : filsToAmount(fils);
};

/** Whole words that denote money. */
const MONEY_WORDS = new Set([
  "amount", "amounts", "balance", "balances", "due", "dues", "fee", "fees",
  "charge", "charges", "price", "prices", "cost", "costs", "deposit", "deposits",
  "payoff", "vat", "paid",
]);

/**
 * Whole words that veto a money match. Percentages must keep their precision -
 * without this, `currentAmountPercent: 33.333333` would be rounded to 33.33 and
 * change what the server recomputes - and dates, counts, ids and types are not
 * amounts even when they sit beside one.
 */
const NOT_MONEY_WORDS = new Set([
  "percent", "percentage", "pct", "rate", "date", "count", "flag", "type",
  "status", "method", "mode", "id", "no", "num", "number",
]);

/** Depth cap - payloads here are shallow; this only bounds pathological input. */
const MAX_PAYLOAD_DEPTH = 8;

/**
 * Splits a property name into lowercase words, handling camelCase, acronyms and
 * snake_case: accountBalance -> [account, balance], VATCharges -> [vat, charges].
 *
 * Matching whole words rather than substrings matters: "account" contains
 * "count", so a substring veto would wrongly exclude accountBalance.
 */
const wordsOf = (key: string): string[] =>
  key
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());

export const isMoneyKey = (key: string): boolean => {
  const words = wordsOf(key);
  if (words.some((word) => NOT_MONEY_WORDS.has(word))) return false;
  return words.some((word) => MONEY_WORDS.has(word));
};

const isPlainObject = (value: object): boolean => {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

/**
 * Walks a request payload and rounds every money-named field to at most 2
 * decimal places, at any nesting depth and in any container.
 *
 * This is the last line of defence before serialization: the gateway rejects a
 * whole request when any money field carries more decimals than the currency
 * has ("money supports only 2 decimal places, but got: 13"). Targeting field
 * names rather than a fixed list of payload shapes means nested arrays such as
 * accounts[], accountList[], transferBalanceAccountList[], checkDetails[] and
 * accountDetails[] are all covered, including API objects whose keys we never
 * enumerated.
 *
 * Values that are not well-formed amounts, and non-plain objects such as File /
 * Blob / FormData, are passed through untouched.
 */
export const normalizePayloadAmounts = (payload: unknown, depth = 0): unknown => {
  if (payload === null || typeof payload !== "object") return payload;
  if (depth >= MAX_PAYLOAD_DEPTH) return payload;

  if (Array.isArray(payload)) {
    return payload.map((item) => normalizePayloadAmounts(item, depth + 1));
  }

  if (!isPlainObject(payload)) return payload;

  const normalized: Record<string, unknown> = {};
  Object.entries(payload as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== null && typeof value === "object") {
      normalized[key] = normalizePayloadAmounts(value, depth + 1);
    } else {
      normalized[key] = isMoneyKey(key) ? normalizeAmountLike(value) : value;
    }
  });

  return normalized;
};

/** Grouped display form for integer fils: 123456 -> "1,234.56". */
export const formatFils = (
  fils: number,
  options?: { locale?: string; withCurrency?: boolean }
): string => {
  const { locale = "en-AE", withCurrency = false } = options ?? {};
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...(withCurrency ? { style: "currency" as const, currency: "AED" } : {}),
  }).format(filsToAmount(fils));
};

/** Grouped display form for a raw amount; malformed values render as "0.00". */
export const formatAmount = (
  value: unknown,
  options?: { locale?: string; withCurrency?: boolean }
): string => formatFils(toFils(value) ?? 0, options);
